import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Pagination,
  CircularProgress,
  Chip,
  Card,
  CardContent,
} from "@mui/material";

import { getCareGapsByPatient } from "../../../../utils/services/CareGapsV2/careGapsV2Service";

const fmtDate = (d) => {
  if (!d) return "—";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return String(d);
  return x.toLocaleDateString();
};

const PAGE_SIZE = 20;

export default function ByPatientTab({ measurementYear }) {
  const [patientName, setPatientName] = useState("");
  const [appliedName, setAppliedName] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [totalPatients, setTotalPatients] = useState(0);
  const [loading, setLoading] = useState(false);
  const [emptyEtl, setEmptyEtl] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    setEmptyEtl(false);
    try {
      const res = await getCareGapsByPatient({
        measurementYear: measurementYear || undefined,
        patientName: appliedName || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list);
      setTotalPatients(Number(res?.pagination?.totalPatients ?? 0));
      if (list.length === 0 && !appliedName) setEmptyEtl(true);
    } catch (e) {
      console.error("Error loading by-patient:", e);
      setRows([]);
      setTotalPatients(0);
    } finally {
      setLoading(false);
    }
  }, [measurementYear, appliedName, page]);

  useEffect(() => { fetch(); }, [fetch]);

  const totalPages = Math.max(1, Math.ceil(totalPatients / PAGE_SIZE));

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <TextField
            size="small"
            label="Search patient"
            placeholder="Last or first name"
            value={patientName}
            onChange={(e) => setPatientName(e.target.value)}
            sx={{ flex: 1, minWidth: 240 }}
          />
          <Button
            variant="contained"
            onClick={() => { setPage(1); setAppliedName(patientName); }}
          >
            Apply
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => { setPatientName(""); setAppliedName(""); setPage(1); }}
          >
            Clear
          </Button>
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {loading ? "Loading…" : `${totalPatients.toLocaleString()} patients with care gaps`}
          </Typography>
        </Stack>
      </Paper>

      {loading && rows.length === 0 ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : emptyEtl && rows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            The pre-computed summary is empty for {measurementYear}.
            Check that the daily ETL has run `SP_REBUILD_CARE_GAP_SUMMARY`.
          </Typography>
        </Paper>
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            No patients match the current filters.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((p) => (
            <Card key={p.cipId} variant="outlined">
              <CardContent>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                      {p.patientFullName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      CIP {p.cipId} · DOB {fmtDate(p.patientDob)} · {p.patientAge}y · {p.patientGender}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      PCP: {p.pcpFullName || "Unassigned"} · Location: {p.locationName || "Unassigned"}
                    </Typography>
                    {(p.patientPhone || p.patientMobile) && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {p.patientPhone ? `📞 ${p.patientPhone}` : ""}
                        {p.patientPhone && p.patientMobile ? "   " : ""}
                        {p.patientMobile ? `📱 ${p.patientMobile}` : ""}
                      </Typography>
                    )}

                    <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                      <Chip size="small" color="error" label={`${p.open} open`} />
                      <Chip size="small" color="warning" label={`${p.submitted} submitted`} />
                      <Chip size="small" color="success" label={`${p.closed} closed`} />
                      <Chip size="small" variant="outlined" label={`${p.total} total`} />
                      {p.historicallyNonCompliant > 0 && (
                        <Chip size="small" sx={{ bgcolor: "#fff3e0", color: "#bf360c" }} label={`⚠ ${p.historicallyNonCompliant} hist. non-compliant`} />
                      )}
                    </Stack>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Last action
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {fmtDate(p.lastWorkflowUpdate)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {totalPages > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            shape="rounded"
            color="primary"
          />
        </Stack>
      )}
    </Box>
  );
}
